import type { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { Coordinate, Entity } from "game-common/models"
import type { ClientRenderable } from "./entity_rendering_manager"
import { entityDepth } from "./entity_depth"
import { applyFloorFall, floorFallForRendering } from "./effects/floor_fall"
import { applyStairTravel, stairTravelForRendering } from "./effects/stair_travel"
import { flightAim, flightDrop, hillDepthOf, hillLiftOf } from "./hill_lift"

/**
 * Placement, animation and depth must agree in every view, including budget-deferred frames. `fromAbove`: drawn on a
 * storey under the viewer's (entityDepth).
 */
export function renderEntityFrame(entity: Entity, sprite: ClientRenderable, roomId?: string,
    canShowArrival = true, now = Date.now(), provider?: TileMapMetaProvider, fromAbove = false): void {
    sprite.updateRenderedLocationFrom(entity)
    // Drawn on the hill under it (hill_lift.ts), before the fall's lift is measured: depth stays at the ground.
    const lift = hillLiftOf(entity, provider)
    if (lift) sprite.y -= lift
    alongHill(entity, sprite, provider)
    const view = floorFallForRendering(entity, sprite, canShowArrival && (sprite.isReadyToShow?.() ?? true), now, roomId)
    sprite.doMovement(stairTravelForRendering(view, sprite, now, roomId))
    const before = sprite.y
    applyFloorFall(view, sprite, now)
    // Sorted from where its fall has it drawn this frame.
    sprite.zIndex = hillDepthOf(entity, lift, entityDepth(view, sprite.defaultZIndex, provider, fromAbove, before - sprite.y))
    applyStairTravel(entity, sprite, now, roomId, lift)
}

/** A shot drawn down or up a hill is turned to the line it is drawn along (Bullet.alongHill). */
export const alongHill = (entity: Entity, sprite: ClientRenderable, provider?: TileMapMetaProvider): void => {
    const drop = flightDrop(entity, provider), aim = drop ? flightAim(entity) : undefined
    if (aim) (sprite as ClientRenderable & { alongHill?(entity: Entity, drop: number, aim: Coordinate): void }).alongHill?.(entity, drop, aim)
}
